import { useEffect, useState } from "react";
import { Formik } from "formik";
import SelectDropdown, { ISelectDropdownItem } from "../../components/form-control/SelectDropdown";
import Button from "../../components/form-control/Button";
import { createBranch, getAccounts, updateBranch } from "../../api/requests";
import Input from "../../components/form-control/Input";
import IntlPhoneInput from "../../components/form-control/IntlPhoneInput/IntlPhoneInput";
import { getRegionsList, PhoneInputValidation } from "../../utilities/miscFunctions";
import { ICreateBranchDTO } from "../../resources/interfaces";
import { toast } from "react-toastify";
import { AxiosError } from "axios";
import PlaceComponent from "../../components/form-control/PlacesInput";
import { Branch } from "../../components/models/branch/Branch";
import * as Yup from "yup";
import TextArea from "../../components/form-control/TextArea";

interface IProps {
    onComplete: (branch: Branch) => void;
    companyId?: string;
    branch?: Branch;
    isEdit?: boolean;
}

const AddBranch = (props: IProps): JSX.Element => {
    const [submitting, setSubmitting] = useState<boolean>(false);

    const initialValues = {
        name: props.branch?.name ?? "",
        city: props.branch?.city ?? "",
        region: props.branch?.region ?? "",
        address: props.branch?.address ?? "",
        phone: props.branch?.phone ?? "",
        email: props.branch?.email ?? ""
    }

    const submitHandler = (values: ICreateBranchFormData) => {
        const payload: ICreateBranchDTO = { ...values, company_id: props.companyId };

        setSubmitting(true);
        if (props.isEdit && props.branch?.id) {
            updateBranch(props.branch.id, payload)
                .then((response: any) => {
                    if (response) {
                        const branch = new Branch(response);
                        props.onComplete(branch);
                    }
                    toast.success(`"Branch updated successfully"`);
                }).catch((error: any | AxiosError | Error) => {
                    toast.error(error?.description, { autoClose: 5000 });
                    console.log("error response", error);
                }).finally(() => setSubmitting(false))
        } else {
            createBranch(payload)
                .then((response: any) => {

                    if (response) {
                        const branch = new Branch(response);
                        props.onComplete(branch);
                    }
                    toast.success(`"Branch added successfully"`);
                }).catch((error: any | AxiosError | Error) => {
                    toast.error(error?.description, { autoClose: 5000 });
                    console.log("error response", error);
                }).finally(() => setSubmitting(false))
        }
    }

    return (
        <div>
            <Formik
                initialValues={initialValues}
                validationSchema={Yup.object({
                    name: Yup.string()
                        .trim()
                        .required('Branch name is required'),
                    city: Yup.string()
                        .trim()
                        .required('City is required'),
                    phone: Yup.string().required("Phone number require"),
                    // ...PhoneInputValidation,
                    region: Yup.string()
                        .trim()
                        .required('Region is required'),
                    address: Yup.string()
                        .trim()
                        .required('Address field is required'),
                })}
                onSubmit={(values: ICreateBranchFormData) => {
                    submitHandler(values);
                }}>
                {({ isSubmitting, values, handleBlur, handleChange, handleSubmit, setFieldValue, touched, errors }) => (
                    <form onSubmit={handleSubmit} className="mt-4">
                        <div className="flex flex-col gap-4">
                            <Input
                                type="text"
                                value={values.name}
                                name="name"
                                label="Branch name"
                                placeholder="Enter name"
                                onBlur={handleBlur}
                                onChange={handleChange}
                                required
                                error={touched.name ? errors.name : undefined
                                }
                                helperText={touched?.name ? errors.name : undefined
                                }
                            />

                            <IntlPhoneInput
                                value={values.phone}
                                label="Phone"
                                placeholder="Enter branch phone"
                                onBlur={handleBlur}
                                onChange={(phone) => {
                                    setFieldValue("phone", phone);
                                }}
                                error={touched.phone ? errors.phone : undefined
                                }
                            // error={touched?.transactor_phone ? errors.transactor_phone : undefined
                            // }
                            />

                            <Input
                                type="text"
                                value={values.email}
                                name="email"
                                label="Branch Email"
                                placeholder="Enter email"
                                onBlur={handleBlur}
                                onChange={handleChange}
                                error={touched.email ? errors.email : undefined
                                }
                                helperText={touched?.email ? errors.email : undefined
                                }
                            />

                            <Input
                                type="text"
                                value={values.city}
                                name="city"
                                label="City"
                                placeholder="Enter city"
                                onBlur={handleBlur}
                                onChange={handleChange}
                                required
                                error={touched.city ? errors.city : undefined
                                }
                                helperText={touched?.city ? errors.city : undefined
                                }
                            />

                            <SelectDropdown
                                list={getRegionsList()}
                                listSelectedValue={values.region}
                                label="Region"
                                placeholder="Select region"
                                wrapperClasses="rounded"
                                onBlur={handleBlur}
                                onChange={(value: string) => {
                                    setFieldValue("region", value);
                                }}
                                required
                                error={touched.region ? errors.region : undefined
                                }
                                helperText={touched?.region ? errors.region : undefined
                                }
                            />

                            <TextArea
                                name="address"
                                label="Address"
                                value={values.address}
                                placeholder="Enter branch address"
                                onBlur={handleBlur}
                                onChange={handleChange}
                                classes="rounded"
                                error={touched.address ? errors.address : undefined
                                }
                                helperText={touched?.address ? errors.address : undefined
                                }
                                required
                            />

                        </div>

                        <div className="flex justify-end mt-8">
                            <Button
                                label="Save"
                                type="submit"
                                disabled={submitting}
                                loading={submitting}
                                className="px-12 bg-secondary-main rounded hover:bg-white hover:border-secondary-main"
                            />
                        </div>
                    </form>
                )}
            </Formik>
        </div>
    )
}

export default AddBranch;

export interface ICreateBranchFormData {
    name: string;
    city: string;
    region: string;
    address: string;
    phone: string;
    email: string;
}